import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { APIClient } from '../../utils/APIClient';
import { API_URL } from '../../settings/local_settings';
import { FaIcon } from '../FaIcon';
import { DeleteInvitationModal } from '../agency/DeleteInvitationModal';
import { YesNoModal } from '../elements/YesNoModal';
import { ClientUsersLimitNotice } from '../invitations/ClientUsersLimitNotice';
import { refetchPortalSubscriptionGating, useClientPortalQuota } from '../../utils/usePortalSubscriptionGating';

// Desde qué fracción del cupo se muestra el contador «N de M usuarios cliente del plan».
const USAGE_VISIBLE_FROM = 0.8;

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Tabla de usuarios con acceso al portal. Los permisos se controlan a nivel del portal
 * (Módulos del portal), por eso acá solo se listan los usuarios + su estado, sin columna
 * de permisos ni edición por-usuario. Estado distingue activos de invitaciones pendientes.
 */
export function PortalUsersTable({ clientId, users, pendingInvitations, onInvite, onRefresh }) {
    const { t } = useTranslation();
    const hasUsers = (users && users.length > 0) || (pendingInvitations && pendingInvitations.length > 0);
    const [removeTarget, setRemoveTarget] = useState(null);
    // Con el cupo lleno se suma la tarjeta del límite con «Mejorar plan». «Invitar» se queda: sumarle
    // este cliente a alguien que YA es usuario del portal no consume cupo, y el modal explica el
    // límite para un email nuevo (C-CUPO-01).
    const quota = useClientPortalQuota();
    const quotaFull = !quota.loading && quota.hasPortal && !quota.canAddClientUser;
    // El contador aparece cerca del tope, que es cuando le sirve a la agencia: con cupos grandes
    // personalizados («1 de 999») era ruido (sugerencia del QA del 26/09).
    const nearLimit = quota.max != null && quota.max > 0 && (quota.current || 0) >= USAGE_VISIBLE_FROM * quota.max;
    const showsUsage = !quota.loading && quota.hasPortal && (quotaFull || nearLimit);
    // Con el cupo lleno la acción que destraba es «Mejorar plan» (en la tarjeta): «Invitar» pasa a
    // secundaria para que no compitan tres naranjas llenos (QA-VIS-cupo-04). Sigue ahí porque sumarle
    // este cliente a alguien que ya usa el portal no consume cupo.
    const inviteClass = quotaFull ? 'btn -alt -negative' : 'btn -alt';

    // El DELETE ya es per-cliente: quitar el acceso ACÁ no lo borra de sus otros clientes.
    // El confirm lo dice cuando el usuario también accede a otros (multi-cliente).
    const confirmRemoveUser = () => {
        const userId = removeTarget.id;
        setRemoveTarget(null);
        new APIClient(API_URL + `client/${clientId}/portal/users/${userId}/`).call('DELETE').then((data) => {
            if (data?.status === 'OK') {
                onRefresh();
                // Quitar un usuario puede liberar un lugar del cupo.
                refetchPortalSubscriptionGating();
            }
        });
    };

    const removeQuestion = (user) => {
        const others = user && user.other_client_names;
        if (others && others.length > 0) {
            return t('portal_tab.remove_user_confirm_multi', {
                clients: others.join(', '),
                interpolation: { escapeValue: false },
            });
        }
        return t('portal_tab.remove_user_confirm');
    };

    return (
        <div className="box portal-tab-users pm-users">
            <div className="box-header portal-tab-users-header">
                <h3 className="box-title">{t('portal_tab.users_section_title')}</h3>
                <div className="portal-tab-users-actions">
                    {showsUsage && (
                        <span className={`portal-tab-users-quota ${quotaFull ? '-full' : ''}`}
                              data-testid="portal-users-quota">
                            {t('portal_tab.client_users_quota', {current: quota.current || 0, max: quota.max})}
                        </span>
                    )}
                    <button className={inviteClass} onClick={onInvite}>
                        <FaIcon iconName="user-plus" />
                        {t('portal_tab.invite_button')}
                    </button>
                </div>
            </div>
            <div className="portal-tab-users-body">
                {quotaFull && (
                    <ClientUsersLimitNotice inline existingUserHint className="portal-tab-users-limit"
                                            current={quota.current} max={quota.max}/>
                )}
                {!hasUsers ? (
                    <div className="portal-tab-empty-users">
                        <FaIcon iconName="users" className="portal-tab-empty-users-icon" />
                        <p>{t('portal_tab.no_users')}</p>
                        <button className="btn -alt -negative" onClick={onInvite}>
                            <FaIcon iconName="user-plus" />
                            {/* Con el cupo lleno, «Invitar al primer usuario» contradice la tarjeta:
                                lo único posible es sumar a alguien que ya usa otro portal. */}
                            {t(quotaFull ? 'portal_tab.add_existing_portal_user' : 'portal_tab.invite_first_user')}
                        </button>
                    </div>
                ) : (
                    <table className="pm-users-table">
                        <thead>
                            <tr>
                                <th>{t('portal_tab.col_email')}</th>
                                <th>{t('portal_tab.col_name')}</th>
                                <th>{t('portal_tab.col_status')}</th>
                                <th className="pm-users-actions-col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {users && users.map(user => (
                                <tr key={user.id}>
                                    <td>
                                        {user.email}
                                        {user.other_client_names && user.other_client_names.length > 0 && (
                                            <span className="pm-status-sub pm-users-also">
                                                {t('portal_tab.also_accesses', {
                                                    clients: user.other_client_names.join(', '),
                                                    interpolation: { escapeValue: false },
                                                })}
                                            </span>
                                        )}
                                    </td>
                                    <td>{user.name && user.name !== user.email ? user.name : <span className="pm-muted">—</span>}</td>
                                    <td>
                                        <span className="portal-tab-active-badge">{t('portal_tab.active_status')}</span>
                                    </td>
                                    <td className="pm-users-actions-col">
                                        <button
                                            className="portal-tab-remove-btn"
                                            onClick={() => setRemoveTarget(user)}
                                            title={t('portal_tab.remove_user')}
                                        >
                                            <FaIcon iconName="xmark" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {pendingInvitations.map(inv => (
                                <tr key={`inv-${inv.id}`} className="portal-tab-pending-row">
                                    <td>{inv.email}</td>
                                    <td><span className="pm-muted">—</span></td>
                                    <td>
                                        <span className="portal-tab-pending-badge">
                                            <FaIcon iconName="clock" />
                                            {t('portal_tab.invitation_pending')}
                                        </span>
                                        {inv.sent_at && (
                                            <span className="pm-status-sub">
                                                {t('portal_tab.col_sent_at')}: {formatDate(inv.sent_at)}
                                            </span>
                                        )}
                                    </td>
                                    <td className="pm-users-actions-col">
                                        <span className="portal-tab-action-btn -delete" title={t('portal_tab.cancel_invitation')}>
                                            <DeleteInvitationModal
                                                invitationId={inv.id}
                                                clientId={clientId}
                                                removeDeletedInvitation={onRefresh}
                                            />
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
            {removeTarget && (
                <YesNoModal
                    isOpen={true}
                    skipRenderButton={true}
                    question={removeQuestion(removeTarget)}
                    onYesAnswer={confirmRemoveUser}
                    onNoAnswer={() => setRemoveTarget(null)}
                />
            )}
        </div>
    );
}
